class MrCarousel extends HTMLElement {
	// MARK: properties.

	#currentIndex = 0;

	#animateDelay = 128; // The transition delay from carousel.css.

	#animating: boolean | null = false;

	#clickHandler = ( e: MouseEvent ): void => {
		// Ignore clicks with modifier keys: shift, ctrl, alt,...
		if ( e.metaKey ) {
			return;
		}

		// Check if target exist and is instance of HTMLElement.
		if ( !e.target || !( e.target instanceof HTMLElement ) ) {
			return;
		}

		// Unknown trigger, not handling this event.
		if (
			!e.target.hasAttribute( 'data-carousel-next' ) &&
			!e.target.hasAttribute( 'data-carousel-previous' )
		) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		if ( e.target.hasAttribute( 'data-carousel-next' ) ) {
			this.goToNextItem();

			return;
		}

		if ( e.target.hasAttribute( 'data-carousel-previous' ) ) {
			this.goToPreviousItem();

			return;
		}
	};

	// MARK: lifecycle.

	connectedCallback() {
		requestAnimationFrame( () => {
			this.querySelectorAll( '[data-carousel-item]:not([data-carousel-item-current])' )?.forEach( ( item ) => {
				const label = item.querySelector( 'a[href]' );
				if ( !label || document.activeElement === label ) {
					return;
				}

				label.setAttribute( 'tabindex', '-1' );
			} );
		} );

		this.addEventListener( 'click', this.#clickHandler );
	}

	disconnectedCallback() {
		this.removeEventListener( 'click', this.#clickHandler );

		// Reset States.
		this.#currentIndex = 0;
		this.#animating = false;
	}

	// MARK: methods.

	private goToPreviousItem() {
		if ( this.#animating ) {
			return;
		}

		const items = this.querySelectorAll( '[data-carousel-item]' );
		const newIndex = indexMinusOne( this.#currentIndex, items.length, true );
		if ( newIndex === this.#currentIndex ) {
			return;
		}

		this.#currentIndex = newIndex;
		this.transition( newIndex );
	}

	private goToNextItem() {
		if ( this.#animating ) {
			return;
		}

		const items = this.querySelectorAll( '[data-carousel-item]' );
		const newIndex = indexPlusOne( this.#currentIndex, items.length, true );
		if ( newIndex === this.#currentIndex ) {
			return;
		}

		this.#currentIndex = newIndex;
		this.transition( newIndex );
	}

	private transition( index: number ) {
		this.#animating = true;

		// Fallback in case the transition events don't work.
		const delayPromise = new Promise( ( resolve ) => {
			setTimeout(
				resolve,
				this.#animateDelay + 50 // Pad with 50ms, this should leave enough time for the animations to run.
			);
		} );

		// Wait for transition to finish.
		const transitionDonePromise = new Promise<void>( ( resolve ) => {
			const carouselTransitionEnd = ( e: TransitionEvent ) => {
				// Check if target exist and is instance of HTMLElement.
				if ( !e.target || !( e.target instanceof HTMLElement ) ) {
					return;
				}

				// check if target has attribute data-carousel-item.
				if ( !e.target.hasAttribute( 'data-carousel-item' ) ) {
					return;
				}

				this.removeEventListener( 'transitionend', carouselTransitionEnd );

				resolve();
			};

			this.addEventListener( 'transitionend', carouselTransitionEnd );
		} );

		// Start the race.
		Promise.race( [
			delayPromise,
			transitionDonePromise,
		] ).then( () => {
			// Render to current index.
			this.updateStateAfterTransition( index );
			// Re-enable transitions.
			this.#animating = false;
		} );
	}

	private updateStateAfterTransition( index: number ) {
		const items = this.querySelectorAll( '[data-carousel-item]' );
		const length = items.length;

		// Check if items not available.
		if ( 2 > length ) {
			return;
		}

		// Get current, previous and next items.
		const previous = items[indexMinusOne( index, length, true )];
		const current = items[index];
		const next = items[indexPlusOne( index, length, true )];

		// Set attributes.
		// First reset all items.
		items.forEach( ( item ) => {
			item.removeAttribute( 'data-carousel-item-previous' );
			item.removeAttribute( 'data-carousel-item-current' );
			item.removeAttribute( 'data-carousel-item-next' );

			const label = item.querySelector( 'a[href]' );
			if ( !label || document.activeElement === label ) {
				return;
			}

			label.setAttribute( 'tabindex', '-1' );
		} );

		previous.setAttribute( 'data-carousel-item-previous', '' );
		next.setAttribute( 'data-carousel-item-next', '' );

		current.setAttribute( 'data-carousel-item-current', '' );

		const counter = this.querySelector( '[data-carousel-counter]' );
		if ( counter ) {
			counter.innerHTML = `${( '00' + ( index + 1 ).toString() ).slice( -2 )} / ${( '00' + ( length ).toString() ).slice( -2 )}`;
		}
	}
}

customElements.define( 'mr-carousel', MrCarousel );

function indexMinusOne( i: number, maxValue: number, looping: boolean ): number {
	if ( 2 > maxValue ) {
		return 0;
	}

	let index = i;

	index--;

	if ( 0 > index ) {
		if ( looping ) {
			return maxValue - 1;
		}

		return 0;

	}

	return index;
}

function indexPlusOne( i: number, maxValue: number, looping: boolean ): number {
	if ( 2 > maxValue ) {
		return 0;
	}

	let index = i;

	index++;

	if ( index >= maxValue ) {
		if ( looping ) {
			return 0;
		}

		return maxValue - 1;

	}

	return index;
}
